import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import set from 'date-fns/set';
import startOfWeek from 'date-fns/startOfWeek';
import { type IntlShape } from 'react-intl-next';

import { OPERATIONS } from '../constants';
import {
	type Operation,
	type OperationIds,
	type RoutingRuleProps,
	type RoutingRulesResponse,
	type TimeRestriction,
} from '../types';

import messages from './messages';
import { type RouteAlertOption } from './types';

export const getOperationName = (id: OperationIds): Operation => {
	const operation = OPERATIONS.find((operation) => operation.id === id);
	return operation?.name as Operation;
};

export const getDays = () => {
	const firstDOW = startOfWeek(new Date(), {
		weekStartsOn: 0,
	});

	const weekDays = Array.from(Array(7)).map((e, i) => ({
		label: format(addDays(firstDOW, i), 'EEE'),
		value: (i % 7) + 1,
	}));

	return weekDays as {
		label: string;
		value: 1 | 2 | 3 | 4 | 5 | 6 | 7;
	}[];
};

export const formatRestriction = (timeInterval: TimeRestriction) => {
	const days = getDays();

	const startDay = days.find((day) => day.value === timeInterval.startDay);
	const endDay = days.find((day) => day.value === timeInterval.endDay);

	const startTime = format(
		set(new Date(), {
			hours: timeInterval.startHour,
			minutes: timeInterval.startMinute,
		}),
		'p',
	);

	const endTime = format(
		set(new Date(), {
			hours: timeInterval.endHour,
			minutes: timeInterval.endMinute,
		}),
		'p',
	);

	if (startDay && endDay) {
		const formattedTime = `${startDay.label} ${startTime} - ${endDay.label} ${endTime}`;

		return formattedTime;
	}

	const formattedTime = `${startTime} - ${endTime}`;

	return formattedTime;
};

export const createRouteAlertOptions = (
	formatMessage: IntlShape['formatMessage'],
	data: RoutingRulesResponse,
	schedules?: RoutingRuleProps['schedules'],
) => {
	const escalationEntities = data.escalations.entities?.map((esc) => ({
		value: esc.id,
		type: 'esc',
		label: esc.name,
		enabled: esc.enabled,
	}));

	const scheduleEntities = schedules?.map((schedule) => ({
		value: schedule.id || '',
		type: 'sch',
		label: schedule.name || '',
		enabled: schedule.enabled,
	}));

	const escalationOptions: RouteAlertOption = {
		label: formatMessage(messages.escalations),
		options: escalationEntities || [],
	};

	const scheduleOptions: RouteAlertOption = {
		label: formatMessage(messages.schedules),
		options: scheduleEntities || [],
	};

	const otherOption = {
		label: formatMessage(messages.other),
		options: [{ value: 'noOne', label: formatMessage(messages.noOne), type: '' }],
	};

	let options = [];

	if (escalationOptions.options.length > 0) {
		options.push(escalationOptions);
	}

	if (scheduleOptions.options.length > 0) {
		options.push(scheduleOptions);
	}

	options.push(otherOption);

	return options;
};

export const getDefaultTimes = () => {
	return [...Array(24).keys()]
		.map((time) => {
			let newTime = time.toString();
			if (newTime.length === 1) {
				newTime = `0${time}`;
			}
			return [`${newTime}:00`, `${newTime}:30`];
		})
		.flatMap((time) => time);
};

export const transformTimeToObject = (startTime: string, endTime: string) => {
	const [startHour, startMinute] = startTime.split(':');
	const [endHour, endMinute] = endTime.split(':');
	return {
		startHour: parseInt(startHour, 10),
		startMinute: parseInt(startMinute, 10),
		endHour: parseInt(endHour, 10),
		endMinute: parseInt(endMinute, 10),
	};
};

export const getMatchType = (conditionMatchType: number) => {
	switch (conditionMatchType) {
		case 1:
			return 'match-any-condition';
		case 2:
			return 'match-all-conditions';
		default:
			return 'match-all';
	}
};
